/**
 * PasswordStrengthIndicator Component
 *
 * Visual password strength meter showing:
 * - Strength bar (weak/medium/strong) using Hesvara brand colors
 * - List of requirements with met/unmet status
 */

import { Check, X } from 'lucide-react';
import {
  validatePassword,
  getPasswordStrength,
  getStrengthLabel,
  type PasswordStrength,
} from '../utils/passwordValidation';

interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
}

/**
 * Get Tailwind classes for strength bar color
 */
function getStrengthColor(strength: PasswordStrength): string {
  switch (strength) {
    case 'strong':
      return 'bg-evergreen';
    case 'medium':
      return 'bg-brass';
    case 'weak':
      return 'bg-red-500';
  }
}

/**
 * Get width percentage for strength bar
 */
function getStrengthWidth(strength: PasswordStrength): string {
  switch (strength) {
    case 'strong':
      return 'w-full';
    case 'medium':
      return 'w-2/3';
    case 'weak':
      return 'w-1/3';
  }
}

/**
 * Get text color for strength label
 */
function getStrengthTextColor(strength: PasswordStrength): string {
  switch (strength) {
    case 'strong':
      return 'text-evergreen';
    case 'medium':
      return 'text-brass-text';
    case 'weak':
      return 'text-red-500';
  }
}

export function PasswordStrengthIndicator({
  password,
  className = '',
}: PasswordStrengthIndicatorProps): JSX.Element {
  const validation = validatePassword(password);
  const strength = getPasswordStrength(password);
  const strengthLabel = getStrengthLabel(strength);

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Strength Bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted">Password strength</span>
          <span className={`text-sm font-medium ${getStrengthTextColor(strength)}`}>
            {password ? strengthLabel : ''}
          </span>
        </div>
        <div className="h-2 bg-soft-neutral rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-250 ${getStrengthColor(strength)} ${getStrengthWidth(strength)}`}
            style={{ opacity: password ? 1 : 0 }}
          />
        </div>
      </div>

      {/* Requirements List */}
      <div className="space-y-1.5">
        <span className="text-sm text-muted">Requirements</span>
        <ul className="space-y-1">
          {validation.requirements.map((req) => (
            <li key={req.id} className="flex items-center gap-2 text-sm">
              {req.met ? (
                <Check className="w-4 h-4 text-evergreen flex-shrink-0" />
              ) : (
                <X className="w-4 h-4 text-red-500 flex-shrink-0" />
              )}
              <span className={req.met ? 'text-muted' : 'text-ink'}>
                {req.label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default PasswordStrengthIndicator;
